import React, { useState, useEffect } from 'react';
import { Pane, Table, Button, Dialog, FileUploader, FileCard, toaster } from 'evergreen-ui';
import './BlogsEdit.css';

function BlogsEdit() {
  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isDialogShown, setIsDialogShown] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileRejections, setFileRejections] = useState([]);
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/blog`);
        if (!response.ok) throw new Error('Failed to fetch blogs');
        const data = await response.json();
        setBlogs(data);
      } catch (error) {
        console.error('Error fetching blogs:', error);
        toaster.danger('Failed to fetch blogs');
      }
    };
    fetchBlogs();
  }, [API_BASE_URL]);

  const handleEdit = (blog) => {
    setSelectedBlog(blog);
    setFiles([]); // Clear files when opening edit dialog
    setFileRejections([]);
    setIsDialogShown(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/blog/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete blog');
      setBlogs(blogs.filter((blog) => blog.id !== id));
      toaster.success('Blog deleted successfully');
    } catch (error) {
      console.error('Error deleting blog:', error);
      toaster.danger('Failed to delete blog');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedBlog((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (files) => {
    setFiles([files[0]]);
    setSelectedBlog((prev) => ({ ...prev, image: files[0] }));
  };

  const handleFileRejected = (fileRejections) => {
    setFileRejections([fileRejections[0]]);
  };

  const handleRemoveFile = () => {
    setFiles([]);
    setFileRejections([]);
    setSelectedBlog((prev) => ({ ...prev, image: null }));
  };

  const handleUpdate = async () => {
    if (!selectedBlog?.title || !selectedBlog?.author || !selectedBlog?.content) {
      toaster.danger('All fields are required.');
      return;
    }

    const uploadData = new FormData();
    uploadData.append('title', selectedBlog.title);
    uploadData.append('author', selectedBlog.author);
    uploadData.append('content', selectedBlog.content);
    if (files[0] && files[0] instanceof File) {
      uploadData.append('image', files[0]);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/blog/${selectedBlog.id}`, {
        method: 'PUT',
        body: uploadData,
      });
      if (!response.ok) throw new Error('Failed to update blog');
      const updatedBlog = await response.json();
      setBlogs(blogs.map((blog) => (blog.id === updatedBlog.id ? updatedBlog : blog)));
      setIsDialogShown(false);
      setFiles([]);
      setFileRejections([]);
      toaster.success('Blog updated successfully');
    } catch (error) {
      console.error('Error updating blog:', error);
      toaster.danger('Failed to update blog');
    }
  };

  return (
    <Pane className="edit-container" padding={16}>
      <Table>
        <Table.Head>
          <Table.TextHeaderCell>Title</Table.TextHeaderCell>
          <Table.TextHeaderCell>Author</Table.TextHeaderCell>
          <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
        </Table.Head>
        <Table.Body>
          {blogs.map((blog) => (
            <Table.Row key={blog.id}>
              <Table.TextCell>{blog.title}</Table.TextCell>
              <Table.TextCell>{blog.author}</Table.TextCell>
              <Table.Cell>
                <Button appearance="primary" onClick={() => handleEdit(blog)} marginRight={8}>
                  Edit
                </Button>
                <Button appearance="primary" intent="danger" onClick={() => handleDelete(blog.id)}>
                  Delete
                </Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <Dialog
        isShown={isDialogShown}
        title="Edit Blog"
        width="90%"
        height="85%"
        onCloseComplete={() => {
          setIsDialogShown(false);
          setFiles([]);
          setFileRejections([]);
        }}
        confirmLabel="Save"
        onConfirm={handleUpdate}
      >
        {selectedBlog && (
          <form className="edit-form">
            <div className="form-row">
              <span className="form-label">Blog Title</span>
              <input
                className="form-input"
                name="title"
                value={selectedBlog.title || ''}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-row">
              <span className="form-label">Author</span>
              <input
                className="form-input"
                name="author"
                value={selectedBlog.author || ''}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-row">
              <span className="form-label">Content</span>
              <textarea
                className="form-input"
                name="content"
                value={selectedBlog.content || ''}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-row">
              <span className="form-label">Blog Image</span>
              <div className="image-container">
                {selectedBlog.image && !(selectedBlog.image instanceof File) && (
                  <img
                    src={`${API_BASE_URL}/${selectedBlog.image}`}
                    alt="Blog Preview"
                    className="form-image"
                  />
                )}
                <Pane maxWidth={654}>
                  <FileUploader
                    label="Upload Image"
                    description="You can upload 1 image file (JPEG, JPG, PNG, BMP, WebP). File can be up to 50 MB."
                    maxSizeInBytes={50 * 1024 ** 2}
                    maxFiles={1}
                    onChange={handleFileChange}
                    onRejected={handleFileRejected}
                    acceptedMimeTypes={[
                      'image/jpeg',
                      'image/jpg',
                      'image/png',
                      'image/bmp',
                      'image/webp',
                    ]}
                    renderFile={(file) => {
                      const { name, size, type } = file;
                      const fileRejection = fileRejections.find(
                        (fileRejection) => fileRejection.file === file
                      );
                      const { message } = fileRejection || {};
                      return (
                        <FileCard
                          key={name}
                          isInvalid={fileRejection != null}
                          name={name}
                          onRemove={handleRemoveFile}
                          sizeInBytes={size}
                          type={type}
                          validationMessage={message}
                        />
                      );
                    }}
                    values={files}
                  />
                </Pane>
              </div>
            </div>
          </form>
        )}
      </Dialog>
    </Pane>
  );
}

export default BlogsEdit;